import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useConfirm, useToast } from '../context/UIContext';
import ServiceReportModal from '../modals/ServiceReportModal';
import {
    deleteServiceReport,
    deleteSignedServiceReport,
    getServiceReportPdf,
    getServiceReportsByProject,
    getSignedServiceReport,
    uploadSignedServiceReport,
} from '../services/ServiceReportService';
import { extractApiError } from '../utils/apiError';
import { formatDate } from '../utils/dateConverter';
import { closePendingTab, openPendingTab, revealBlobInTab } from '../utils/pdfTab';
import { fmtDuration, signedFileError, SIGNED_TYPES } from '../utils/serviceReport';
import '../styles/ServiceReports.css';

function period(report) {
    if (!report.first_date) return '—';
    const first = formatDate(report.first_date);
    if (!report.last_date || report.last_date === report.first_date) return first;
    const last = formatDate(report.last_date);
    return first.slice(6) === last.slice(6) ? `${first.slice(0, 6)} – ${last}` : `${first} – ${last}`;
}

/** Serviceberichte of one project: write, print for signature, file the signed scan. */
export default function ServiceReports({ project }) {
    const toast = useToast();
    const confirm = useConfirm();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [editing, setEditing] = useState(undefined);
    const [busyId, setBusyId] = useState(null);
    const fileInput = useRef(null);
    const uploadTarget = useRef(null);

    const load = useCallback(async () => {
        if (!project?.id) return;
        try {
            setReports(await getServiceReportsByProject(project.id));
            setLoadError('');
        } catch (err) {
            setLoadError(extractApiError(err, 'Serviceberichte konnten nicht geladen werden.'));
        } finally {
            setLoading(false);
        }
    }, [project?.id]);

    useEffect(() => {
        setLoading(true);
        setReports([]);
        load();
    }, [load]);

    const replaceRow = (updated) => setReports(cur => cur.map(r => (r.id === updated.id ? updated : r)));
    // Only clears the slot if it is still this row's, so a second action keeps its own busy state.
    const release = (id) => setBusyId(cur => (cur === id ? null : cur));

    const openBlob = async (report, message, fetchBlob, failure) => {
        const tab = openPendingTab(message);
        if (!tab) {
            toast('Der Browser hat das neue Fenster blockiert — bitte Popups erlauben.');
            return;
        }
        setBusyId(report.id);
        try {
            revealBlobInTab(tab, await fetchBlob(report.id));
        } catch (err) {
            closePendingTab(tab);
            toast(extractApiError(err, failure));
        } finally {
            release(report.id);
        }
    };

    const openPdf = (report) => openBlob(
        report, `Servicebericht ${report.report_number} wird erstellt…`,
        getServiceReportPdf, 'PDF konnte nicht erstellt werden.',
    );

    const openSigned = (report) => openBlob(
        report, `Servicebericht ${report.report_number} wird geladen…`,
        getSignedServiceReport, 'Der unterschriebene Bericht konnte nicht geladen werden.',
    );

    const pickSignedFile = (report) => {
        uploadTarget.current = report;
        if (fileInput.current) {
            fileInput.current.value = '';
            fileInput.current.click();
        }
    };

    const onFileChosen = async (e) => {
        const file = e.target.files?.[0];
        const report = uploadTarget.current;
        if (!file || !report) return;
        const problem = signedFileError(file);
        if (problem) { toast(problem); return; }
        setBusyId(report.id);
        try {
            replaceRow(await uploadSignedServiceReport(report.id, file));
            toast(`Unterschriebener Bericht zu ${report.report_number} gespeichert.`);
        } catch (err) {
            toast(extractApiError(err, 'Hochladen fehlgeschlagen.'));
        } finally {
            release(report.id);
        }
    };

    const removeSigned = async (report) => {
        const ok = await confirm(
            `Den unterschriebenen Scan zu ${report.report_number} löschen? Der Bericht ist danach wieder bearbeitbar.`,
            { confirmLabel: 'Scan entfernen', danger: true },
        );
        if (!ok) return;
        setBusyId(report.id);
        try {
            replaceRow(await deleteSignedServiceReport(report.id));
        } catch (err) {
            toast(extractApiError(err, 'Der Scan konnte nicht entfernt werden.'));
        } finally {
            release(report.id);
        }
    };

    const remove = async (report) => {
        const ok = await confirm(
            report.is_signed
                ? `Servicebericht ${report.report_number} „${report.title}“ ist unterschrieben. Beim Löschen wird auch der hochgeladene Scan gelöscht. Wirklich löschen?`
                : `Servicebericht ${report.report_number} „${report.title}“ wirklich löschen?`,
            { confirmLabel: 'Löschen', danger: true },
        );
        if (!ok) return;
        setBusyId(report.id);
        try {
            await deleteServiceReport(report.id);
            setEditing(undefined);
            setReports(cur => cur.filter(r => r.id !== report.id));
        } catch (err) {
            toast(extractApiError(err, 'Servicebericht konnte nicht gelöscht werden.'));
        } finally {
            release(report.id);
        }
    };

    const onSaved = (saved) => {
        setEditing(undefined);
        setReports(cur => (cur.some(r => r.id === saved.id)
            ? cur.map(r => (r.id === saved.id ? saved : r))
            : [saved, ...cur]));
    };

    return (
        <section className="sr-card" aria-labelledby="sr-heading">
            <div className="sr-card-head">
                <h4 id="sr-heading" className="sr-card-title">
                    Serviceberichte{reports.length > 0 && <span className="sr-count">{reports.length}</span>}
                </h4>
                <button type="button" className="button btn-sm" onClick={() => setEditing(null)}>
                    + Servicebericht
                </button>
            </div>

            <input
                ref={fileInput}
                type="file"
                accept={SIGNED_TYPES.join(',')}
                className="sr-file-input"
                data-testid="sr-file-input"
                onChange={onFileChosen}
            />

            {loadError && <p className="sr-empty sr-empty--error">{loadError}</p>}
            {!loadError && loading && <p className="sr-empty">Wird geladen…</p>}
            {!loadError && !loading && reports.length === 0 && (
                <p className="sr-empty">Noch keine Serviceberichte für dieses Projekt.</p>
            )}

            {reports.length > 0 && (
                <div className="sr-table-wrap">
                    <table className="table table--cards sr-table">
                        <thead>
                            <tr>
                                <th>Bericht</th>
                                <th>Datum</th>
                                <th>Anlass</th>
                                <th>Aufenthalt</th>
                                <th aria-label="Aktionen" />
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map(report => {
                                const busy = busyId === report.id;
                                return (
                                    <tr key={report.id}>
                                        <td data-label="Bericht">
                                            <span className="sr-number">
                                                {report.report_number}
                                                {report.language === 'en' && (
                                                    <span className="sr-lang" title="PDF auf Englisch">EN</span>
                                                )}
                                            </span>
                                            {report.is_signed ? (
                                                <span className="status-badge status-closed sr-status"
                                                    title={report.signed_file_name || undefined}>
                                                    Unterschrieben
                                                </span>
                                            ) : (
                                                <span className="status-badge status-open sr-status">
                                                    Unterschrift fehlt
                                                </span>
                                            )}
                                        </td>
                                        <td data-label="Datum" className="sr-date">{period(report)}</td>
                                        <td data-label="Anlass">
                                            {report.title}
                                            {report.technician_name && (
                                                <span className="sr-sub">{report.technician_name}</span>
                                            )}
                                        </td>
                                        <td data-label="Aufenthalt" title={`Arbeitszeit ${fmtDuration(report.work_minutes)}`}>
                                            {fmtDuration(report.on_site_minutes)}
                                        </td>
                                        <td className="table-card-actions">
                                            <div className="sr-actions">
                                                <button type="button" className="button btn-sm" disabled={busy}
                                                    onClick={() => openPdf(report)}>
                                                    PDF
                                                </button>
                                                {report.is_signed ? (
                                                    <>
                                                        <button type="button" className="button button-normal btn-sm"
                                                            disabled={busy} onClick={() => openSigned(report)}>
                                                            Scan ansehen
                                                        </button>
                                                        <button type="button" className="button button-normal btn-sm"
                                                            disabled={busy} onClick={() => removeSigned(report)}>
                                                            Scan entfernen
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button type="button" className="button button-normal btn-sm"
                                                            disabled={busy} onClick={() => setEditing(report)}>
                                                            Bearbeiten
                                                        </button>
                                                        <button type="button" className="button button-normal btn-sm"
                                                            disabled={busy}
                                                            title="Unterschriebenen Bericht hochladen (PDF, PNG oder JPEG)"
                                                            onClick={() => pickSignedFile(report)}>
                                                            Scan hochladen
                                                        </button>
                                                    </>
                                                )}
                                                <button type="button" className="button button-danger btn-sm"
                                                    disabled={busy} onClick={() => remove(report)}>
                                                    Löschen
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {editing !== undefined && (
                <ServiceReportModal
                    project={project}
                    report={editing}
                    onClose={() => setEditing(undefined)}
                    onSaved={onSaved}
                    onDelete={editing ? () => remove(editing) : undefined}
                />
            )}
        </section>
    );
}
